import { Injectable, Renderer2 } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ClientRequestsBreakpoint, ClientRequestsLayout } from '../models';
import { debounceTime, delay, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { ignoreNullish, invert, isEmpty, NonemptyPartial } from '@aiwyn/shared';

const SELECTION_TRANSITION_DURATION = 250;

export type DimensionTransitionClasses = NonemptyPartial<RawDimensionTransitionClasses>;

export interface RawDimensionTransitionClasses {
  regular: DimensionTransitionClassSet;
  inverse: DimensionTransitionClassSet;
}

export interface DimensionTransitionClassSet {
  height: string;
  width: string;
  both: string;
}

interface DimensionTransitionElements {
  height: HTMLElement[];
  width: HTMLElement[];
}

/**
 * There are 2 types of transitions:
 *   1. Regular: Occurs for elements that are already shown before a client request group has been selected. Some common transitions for
 *      these elements are "shrink", "grow", or "fade-out". They do the opposite transition when the client request group is deselected.
 *      The reason these are called 'regular' transitions is because the transition classes are applied when selection is active.
 *   2. Inverse: Occurs for elements not shown until a client request group has been selected. Some common transitions for these elements
 *      are "appear (grow)" and "fade-in". They do the opposite transition when the client request group is deselected. The reason these
 *      are called 'inverse' transitions is because the transition classes are applied when selection is NOT active.
 *
 * Static dimensions are set on elements whose height and/or width is transitioned. This occurs when a selection change occurs and must be
 * completed before any transition classes can be applied (to ensure synchronized transitions). Because of this, it is important to avoid
 * using conditions outside of this service to determine when to apply transition classes.
 *
 * Example of applying regular transition classes to an element:
 * ```
 *   <button
 *     class="cxp-transition cxp-dimension-transition"
 *     [ngClass]="{ 'cxp-shrink cxp-fade-out': applyTransitionClasses$ | async }"
 *     [attr.aria-hidden]="applyTransitionClasses$ | async"
 *   >
 *     I disappear on group selection!
 *   </button>
 * ```
 *
 * Generally, you should apply "cxp-transition" class to any element that will have transition classes applied to it. Or you can manually
 * define the transition styles in your CSS. You also need to apply one of the defined 'regular' dimension transition classes so this
 * service can access it to apply static dimensions before the transition. In this example, the 'cxp-dimension-transition' class is applied.
 * This signals to this service that both height and width transitions are being applied to the element. Once that is complete,
 * applyTransitionClasses$ will emit a value and the transition classes will be applied to the element.
 *
 * Example of applying inverse transition classes to an element:
 * ```
 *   <button
 *     class="cxp-transition cxp-width-transition-inverse"
 *     [ngClass]="{ 'cxp-shrink-width cxp-fade-out': applyInverseTransitionClasses$ | async }"
 *     [attr.aria-hidden]="applyInverseTransitionClasses$ | async"
 *   >
 *     I appear on group selection!
 *   </button>
 * ```
 *
 * In this example, the 'cxp-width-transition-inverse' class is applied to signal that only width transitions are being applied to the
 * element. The inverse transition classes will be applied until a client request group is selected.
 *
 * You may run into competing classes, especially when styles are !important. In this case, it may be best to adjust your approach to
 * something like this:
 * ```
 *   <label
 *     class="w-100 text-center cxp-transition"
 *     [ngClass]="
 *       (applyTransitionClasses$ | async)
 *         ? 'h1-600 color-black cursor-text extra-large-header'
 *         : 'h3-500 color-primary-400 cursor-pointer truncate'
 *     "
 *   >
 *     {{ name$ | async }}
 *   </label>
 * ```
 *
 * This way, you can avoid competing classes and still apply the necessary styles. If this approach does not fit your use case, then you
 * may need to apply new classes to the element and adjust the CSS accordingly. This usually occurs when using certain global or bootstrap
 * classes, and in most cases, the initial class applied overtakes the one applied for the transition.
 *
 * While it isn't normally best practice to set static heights and widths on elements whose dimensions can be determined by their content,
 * it is necessary for most, if not all, elements that have an 'inverse' transition. This is because the size of the element's content
 * cannot be determined until the transition is complete. There are several ways to handle this, but you will see throughout this feature
 * examples like this:
 * ```
 *   <button
 *     style="height: 34px;"
 *     class="w-fit-content cxp-transition"
 *     [ngClass]="{ 'cxp-shrink-height cxp-fade-out': applyInverseTransitionClasses$ | async }"
 *     [attr.aria-hidden]="applyInverseTransitionClasses$ | async"
 *     (click)="onGoBack($event)"
 *   >
 *     Go back
 *   </button>
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ClientRequestsLayoutService {
  readonly layout$: Observable<ClientRequestsLayout> = this.breakpointObserver.observe(ClientRequestsBreakpoint.FULL).pipe(
    map(({ matches }) => matches),
    distinctUntilChanged(),
    map(matchesFullLayout => matchesFullLayout ? ClientRequestsLayout.FULL : ClientRequestsLayout.COMPACT),
    shareReplay(1),
  );

  private readonly _host$ = new BehaviorSubject<HTMLElement>(undefined);
  private _renderer: Renderer2;
  private _transitionCondition$: Observable<boolean>;

  private readonly _applyTransitionClasses$ = new BehaviorSubject<boolean>(false);
  readonly applyTransitionClasses$ = this._applyTransitionClasses$.asObservable();
  readonly applyInverseTransitionClasses$ = this._applyTransitionClasses$.pipe(
    invert(),
    shareReplay(1),
  );

  private _dimensionTransitionClasses: DimensionTransitionClasses;

  readonly setStaticDimensionsOnTransitionElements$ = this._host$.pipe(
    ignoreNullish(),
    switchMap(_ => this._transitionCondition$),
    distinctUntilChanged(),
    filter(isSelected => isSelected !== this._applyTransitionClasses$.value),
    delay(1),
    tap(isSelected => this._onTransitionStart(isSelected)),
    debounceTime(SELECTION_TRANSITION_DURATION),
    tap(isSelected => this._onTransitionEnd(isSelected)),
  );

  private _onTransitionStart(isSelected: boolean): void {
    this._applyTransitionClasses$.next(isSelected);

    if (isSelected) {
      const transitionElements = this._getTransitionElements('regular');
      this._setDimensionsOnElements(transitionElements);
    } else {
      const inverseTransitionElements = this._getTransitionElements('inverse');
      this._setDimensionsOnElements(inverseTransitionElements);
    }
  }

  private _onTransitionEnd(isSelected: boolean): void {
    if (isSelected) {
      const inverseTransitionElements = this._getTransitionElements('inverse');
      this._removeDimensionsFromElements(inverseTransitionElements);
    } else {
      const transitionElements = this._getTransitionElements('regular');
      this._removeDimensionsFromElements(transitionElements);
    }
  }

  private _getTransitionElements(transitionType: keyof DimensionTransitionClasses): DimensionTransitionElements {
    if (isEmpty(this._dimensionTransitionClasses[transitionType])) {
      return { height: [], width: [] };
    }

    const { height, width, both } = this._dimensionTransitionClasses[transitionType];
    const heightTransitionElements = Array.from(this._host$.value.getElementsByClassName(height));
    const widthTransitionElements = Array.from(this._host$.value.getElementsByClassName(width));
    const bothTransitionElements = Array.from(this._host$.value.getElementsByClassName(both));

    return {
      height: heightTransitionElements.concat(bothTransitionElements) as HTMLElement[],
      width: widthTransitionElements.concat(bothTransitionElements) as HTMLElement[],
    };
  }

  private _setDimensionsOnElements(elements: DimensionTransitionElements): void {
    elements.height.forEach(element => {
      this._renderer.setStyle(element, 'height', `${element.offsetHeight}px`);
      this._renderer.setStyle(element, 'overflow-y', 'hidden');
    });

    elements.width.forEach(element => {
      this._renderer.setStyle(element, 'width', `${element.offsetWidth}px`);
      this._renderer.setStyle(element, 'overflow-x', 'hidden');
    });
  }

  private _removeDimensionsFromElements(elements: DimensionTransitionElements): void {
    elements.height.forEach(element => {
      this._renderer.removeStyle(element, 'height');
      this._renderer.removeStyle(element, 'overflow-y');
    });

    elements.width.forEach(element => {
      this._renderer.removeStyle(element, 'width');
      this._renderer.removeStyle(element, 'overflow-x');
    });
  }

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
  ) {}

  startTransitionManager(
    host: HTMLElement,
    transitionCondition$: Observable<boolean>,
    renderer: Renderer2,
    dimensionTransitionClasses: DimensionTransitionClasses,
  ): Observable<unknown> {
    this._host$.next(host);
    this._transitionCondition$ = transitionCondition$;
    this._renderer = renderer;
    this._dimensionTransitionClasses = dimensionTransitionClasses;

    return this.setStaticDimensionsOnTransitionElements$;
  }
}
